import {isMobile, isTablet} from 'Utils/deviceUtils';
import {getDocumentScrollTop, scrollTo} from 'Utils/scrollUtils';

export default class GuaranteeSummary {
    constructor(domElement) {
        if(!domElement) {
            throw new DOMException('element is undefined');
        }

        if(domElement.dataset.guaranteeSummaryInitialized === 'true') {
            return;
        }

        domElement.guaranteeSummaryCtl = this;
        domElement.dataset.guaranteeSummaryInitialized = 'true';

        this.domElement = domElement;
        this.setDefaults();

        this.analyzePage();
        this.registerEvents();
    }

    setDefaults() {
        this._categoryRefs = [];
        this._categoryOffsets = [];
        this._contentStartOffset = 0;
        this._stickyHeight = 0;
    }

    /**
     * @description Scan the current page to see if there are any guarantee category sections.
     * */
    analyzePage() {
        const domContentElement = this.domElement.querySelector('.toggle-content');
        let contentElementHTML = '';

        [...document.querySelectorAll('.guarantee-category:not(.parbase)')].forEach((domCategoryElement, categoryElementIndex) => {
            const categoryRef = domCategoryElement.getAttribute('id');
            const domCategoryTitleElement = domCategoryElement.querySelector('h2') || {};
            const categoryTitle = domCategoryTitleElement.textContent;
            const isReadString = categoryElementIndex === 0 ? 'class="read last"' : '';

            this._categoryRefs.push(categoryRef);
            this._categoryOffsets.push(domCategoryElement.offsetTop);

            contentElementHTML += `<li ${isReadString}><div class="marker"><span class="circle"></span><span class="line"></span><span class="line progress"></span></div><a href="#${categoryRef}">${categoryTitle}</a></li>`;
        }, this);

        domContentElement.innerHTML = contentElementHTML;

        // TODO WTF an empty timeout
        window.setTimeout(() => {
            this.refreshStickyHeight();
            this.refreshContentStartOffset();
            this.refreshCategoryOffsets();
        });
    }

    refreshStickyHeight() {
        this._stickyHeight = this.domElement.querySelector('.toggle-label').offsetHeight;
    }

    refreshContentStartOffset() {
        const headerHeight = document.querySelector('.js-header').offsetHeight;
        const bannerHeight = document.querySelector('.js-page-banner').offsetHeight;

        this._contentStartOffset = headerHeight + bannerHeight;
    }

    refreshCategoryOffsets() {
        [...document.querySelectorAll('.guarantee-category:not(.parbase)')].forEach((domCategoryElement, categoryElementIndex) => {
            this._categoryOffsets[categoryElementIndex] = domCategoryElement.offsetTop;
        }, this);
    }

    registerEvents() {
        window.addEventListener(
            'scroll',
            this.handlePageScroll.bind(this),
            true
        );

        window.addEventListener('resize', () => {
            this.refreshStickyHeight();
            this.refreshContentStartOffset();
            this.refreshCategoryOffsets();
        }, 500);

        [...document.querySelectorAll('.guarantee-category:not(.parbase) input[type="checkbox"].toggle')].forEach(domToggleElement => {
            domToggleElement.addEventListener('change', () => {
                setTimeout(this.refreshCategoryOffsets, 500);
            });
        }, this);

        // Nav element click to animate the scroll movement
        [...this.domElement.querySelectorAll('.toggle-content li a')].forEach((domNavElement, navElementIndex) => {
            domNavElement.dataset.summaryIndex = navElementIndex.toString();

            domNavElement.addEventListener('click', this.handleNavElementClick.bind(this));
        }, this);
    }

    adjustContentHeightForDesktop(content, contentHeight, scrollTop, sidebar) {
        if(contentHeight < scrollTop) {
            sidebar.style.height = `${content.offsetHeight}px`;
            this.domElement.classList.add('at-bottom');
        } else {
            this.domElement.classList.remove('at-bottom');
        }
    }

    handleStickyBehavior() {
        const scrollTop = getDocumentScrollTop();
        const sidebar = document.querySelector('[data-sidebar]');

        if(scrollTop > this._contentStartOffset) {
            this.domElement.classList.add('sticky-sum');

            sidebar.style.height = `${this._stickyHeight}px`;

            if(!isMobile() && !isTablet()) {
                const content = document.querySelector('[data-content]'),
                    contentHeight = document.querySelector('[data-columns]').offsetHeight;

                this.adjustContentHeightForDesktop(content, contentHeight, scrollTop, sidebar);
            }
        } else {
            this.domElement.classList.remove('sticky-sum');
            sidebar.style.height = 'auto';
        }
    }

    // eslint-disable-next-line class-methods-use-this
    nextItemDisplay(target, lastElement) {
        let nextLastElement = lastElement;

        if(!target.classList.contains('read')) {
            target.classList.add('read');
            target.classList.add('last');

            lastElement && lastElement.classList.remove('last');

            nextLastElement = target;
        } else if(target.classList.contains('last') && lastElement === null) {
            nextLastElement = target;
        }

        return nextLastElement;
    }

    changeClassListsOnElement({progress, target, refIndex, readPosition, readProgress, lastElement}) {
        let nextLastElement;
        if(this._categoryOffsets[refIndex] <= readPosition) {
            nextLastElement = this.nextItemDisplay(target, lastElement);

            progress.style.height = `${readProgress}%`;
        } else {
            if(refIndex !== 0 && target.classList.contains('read')) {
                target.classList.remove('read');
                target.classList.remove('last');

                target.previousSibling.classList.add('last');
            }

            progress.style.height = '0';
        }

        return nextLastElement;
    }

    handleReadProgressBehavior() {
        const readPosition = getDocumentScrollTop() - this._contentStartOffset;
        let lastElement = null;

        this._categoryRefs.forEach((refString, refIndex) => {
            const target = (this.domElement.querySelector(`[href="#${refString}"]`) || {}).parentNode,
                progress = target && target.querySelector('.progress'),
                maxPix = this._categoryOffsets[refIndex + 1] - this._categoryOffsets[refIndex],
                currentPix = Math.max(0, readPosition - this._categoryOffsets[refIndex]);

            const readProgress = Math.min((currentPix * 100) / maxPix, 100) || 0;

            if(target) {
                lastElement = this.changeClassListsOnElement({
                    lastElement,
                    progress,
                    readPosition,
                    readProgress,
                    refIndex,
                    target
                });
            }
        }, this);
    }

    handlePageScroll() {
        this.handleStickyBehavior();
        this.handleReadProgressBehavior();
    }

    tryToScroll(navIndex) {
        const from = getDocumentScrollTop();
        // eslint-disable-next-line id-length
        const to = this._categoryOffsets[navIndex] + this._contentStartOffset;
        // 30 => css top - .two-column padding top

        scrollTo(from, to, 500);
    }

    handleNavElementClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        this.refreshCategoryOffsets();

        try {
            const navIndex = parseInt(event.currentTarget.dataset.summaryIndex, 10);
            const domElement = this.domElement.querySelector('.js-toggle'),
                domIcon = this.domElement.querySelector('.js-icon');

            this.tryToScroll(navIndex);

            domElement.classList.add('hidden-sm');
            domIcon.classList.remove('rotate-45');
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }
}
